import { useState, useEffect } from "react";
import {
  ShoppingWidget as AppShoppingWidget,
  AIFixCard as AppAIFixCard,
  MagicFeedLogo,
  ScanProductCard,
  ProductCard,
  SCAN_PRODUCTS,
} from "./App";
import {
  ShoppingWidget as FPShoppingWidget,
  AIFixCard as FPAIFixCard,
  ImpactCascade,
  TitleRewriteAnim,
  DescriptionGrowAnim,
  HighlightsAnim,
  IntroducingLogo,
  ExitPopupAnim,
} from "./FinalPage";
import AdsCreatives from "./AdsCreatives";
import FacebookAds from "./FacebookAds";
import FacebookAdsV2 from "./FacebookAdsV2";

/* ── Looping wrappers for non-looping components ── */

function LoopingAppAIFix() {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle(c => c + 1), 14000);
    return () => clearInterval(t);
  }, []);
  return <AppAIFixCard key={cycle} started={true} />;
}

function LoopingScanDemo() {
  const product = SCAN_PRODUCTS[0];
  const [phase, setPhase] = useState(0);
  const [rowScores, setRowScores] = useState([0, 0, 0]);

  useEffect(() => {
    let running = true;
    const wait = ms => new Promise(r => setTimeout(r, ms));
    const loop = async () => {
      while (running) {
        setPhase(0); setRowScores([0, 0, 0]);
        await wait(1000); if (!running) break;
        setPhase(1);
        await wait(2500); if (!running) break;
        setPhase(2);
        setRowScores([product.titleScore, product.descScore, 1]);
        await wait(4000); if (!running) break;
      }
    };
    loop();
    return () => { running = false; };
  }, [product.titleScore, product.descScore]);

  return (
    <ScanProductCard
      product={product}
      scanning={phase === 1}
      showScore={phase >= 2}
      visibleRows={phase >= 2 ? 3 : 0}
      rowScores={rowScores}
    />
  );
}

function LoopingProductCardDemo() {
  const TITLE = 'Premium & Durable Luxury Laptop Backpack \u2014 15.6"';
  const [title, setTitle] = useState("Laptop backpack");
  const [optimized, setOptimized] = useState(false);
  const [ranked, setRanked] = useState(false);

  useEffect(() => {
    let running = true;
    const wait = ms => new Promise(r => setTimeout(r, ms));
    const loop = async () => {
      while (running) {
        setTitle("Laptop backpack"); setOptimized(false); setRanked(false);
        await wait(2000); if (!running) break;
        setOptimized(true); setTitle("");
        for (let i = 1; i <= TITLE.length; i++) {
          if (!running) break;
          setTitle(TITLE.slice(0, i));
          await wait(25);
        }
        if (!running) break;
        await wait(600);
        setRanked(true);
        await wait(4000); if (!running) break;
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ maxWidth: 220, margin: "0 auto" }}>
      <ProductCard isOur={true} title={title} price="$89.99" optimized={optimized} ranked={ranked} dim={false} />
    </div>
  );
}

function LoopingTitleRewrite() {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle(c => c + 1), 8000);
    return () => clearInterval(t);
  }, []);
  return <TitleRewriteAnim key={cycle} started={true} />;
}

function LoopingDescGrow() {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle(c => c + 1), 8000);
    return () => clearInterval(t);
  }, []);
  return <DescriptionGrowAnim key={cycle} started={true} />;
}

function LoopingHighlights() {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle(c => c + 1), 8000);
    return () => clearInterval(t);
  }, []);
  return <HighlightsAnim key={cycle} started={true} />;
}

/* ── Gallery card wrapper ── */
function GalleryCard({ title, description, children }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      overflow: "hidden",
    }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f1f1" }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", margin: 0 }}>{title}</h3>
        <p style={{ fontSize: 11, color: "#6b7280", margin: "4px 0 0" }}>{description}</p>
      </div>
      <div style={{ padding: 20 }}>
        {children}
      </div>
    </div>
  );
}

/* ── Gallery items ── */
const GALLERY = [
  {
    title: "Shopping Widget (App)",
    desc: "Google Shopping cards with scan \u2192 rewrite \u2192 rank #1",
    render: () => <AppShoppingWidget />,
  },
  {
    title: "AI Fix Card (App)",
    desc: "Issues detected \u2192 fixed one by one, score 32\u219298 with confetti",
    render: () => <LoopingAppAIFix />,
  },
  {
    title: "Scan Product Card",
    desc: "Product card with vertical scan line animation",
    render: () => <LoopingScanDemo />,
  },
  {
    title: "Product Card",
    desc: "Product card with typing optimized title",
    render: () => <LoopingProductCardDemo />,
  },
  {
    title: "MagicFeed Logo",
    desc: "Animated floating logo",
    render: () => (
      <div style={{ animation: "floatMF 4s ease-in-out infinite", textAlign: "center", padding: "20px 0" }}>
        <MagicFeedLogo centered />
      </div>
    ),
  },
  {
    title: "Shopping Widget (Final)",
    desc: "FinalPage version with scan + rank animation",
    render: () => <FPShoppingWidget />,
  },
  {
    title: "AI Fix Card (Final)",
    desc: "FinalPage version with looping score animation",
    render: () => <FPAIFixCard />,
  },
  {
    title: "Impact Cascade",
    desc: "+67% impressions \u2192 +43% clicks \u2192 +38% revenue, sequential cascade",
    render: () => <ImpactCascade />,
  },
  {
    title: "Title Rewrite Animation",
    desc: 'Typing effect: "Laptop bag" \u2192 "Premium Luxury Laptop Backpack 15.6\u2033"',
    render: () => <LoopingTitleRewrite />,
  },
  {
    title: "Description Grow Animation",
    desc: "Progress bar growing from 120 \u2192 4970 chars",
    render: () => <LoopingDescGrow />,
  },
  {
    title: "Highlights Animation",
    desc: "Checkmarks appearing one by one (Material, Fit, Care...)",
    render: () => <LoopingHighlights />,
  },
  {
    title: "Introducing Logo",
    desc: '"Introducing" + logo with orbital sparkles',
    render: () => <IntroducingLogo />,
  },
  {
    title: "Exit Popup Animation",
    desc: "Circular score gauge 32\u219298 with status change",
    render: () => <ExitPopupAnim />,
  },
];

export default function CreasPage() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes floatMF { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes pulseMF { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes shimmerMF { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes popIn { 0%{opacity:0;transform:scale(0.75)} 100%{opacity:1;transform:scale(1)} }
        @keyframes confettiBurst { 0%{opacity:1;transform:translateY(0) scale(1)} 100%{opacity:0;transform:translateY(-60px) scale(0.5)} }
        @keyframes scanDown { 0% { top: 0; } 100% { top: 100%; } }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes sparkleOrbit1 { 0%{transform:rotate(0deg) translateX(52px) rotate(0deg) scale(0.8)} 50%{transform:rotate(180deg) translateX(52px) rotate(-180deg) scale(1.2)} 100%{transform:rotate(360deg) translateX(52px) rotate(-360deg) scale(0.8)} }
        @keyframes sparkleOrbit2 { 0%{transform:rotate(120deg) translateX(60px) rotate(-120deg) scale(1)} 50%{transform:rotate(300deg) translateX(60px) rotate(-300deg) scale(0.7)} 100%{transform:rotate(480deg) translateX(60px) rotate(-480deg) scale(1)} }
        @keyframes sparkleOrbit3 { 0%{transform:rotate(240deg) translateX(48px) rotate(-240deg) scale(0.9)} 50%{transform:rotate(420deg) translateX(48px) rotate(-420deg) scale(1.3)} 100%{transform:rotate(600deg) translateX(48px) rotate(-600deg) scale(0.9)} }
        .mf-hide-mobile { display: block; }
        .mf-fix-layout { display: flex; }
        .mf-cascade { padding: 28px; }

        @media (max-width: 768px) {
          .creas-grid { grid-template-columns: 1fr !important; }
          .mf-fix-layout { flex-direction: column !important; }
          .mf-fix-layout .mf-fix-divider { width: 100% !important; height: 1px !important; }
          .mf-fix-layout .mf-fix-score { width: 100% !important; padding: 16px 20px !important; }
          .mf-cascade { padding: 20px 12px !important; }
        }

        @media (max-width: 480px) {
          .mf-hide-mobile { display: none !important; }
          .mf-product-card-f { height: 260px !important; }
          .mf-product-img-f { height: 75px !important; }
          .mf-ai-badge-f { height: 16px !important; margin-bottom: 3px !important; }
          .mf-ai-badge-f span { font-size: 7px !important; padding: 1px 5px !important; }
        }
      `}</style>

      <div style={{
        minHeight: "100vh",
        fontFamily: "'Inter',sans-serif",
        background: "linear-gradient(135deg,#f0f4ff 0%,#e8eeff 50%,#dde5ff 100%)",
        padding: "40px 20px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h1 style={{
            fontSize: 28, fontWeight: 800, color: "#0f172a",
            textAlign: "center", marginBottom: 8,
          }}>
            MagicFeedPro &mdash;{" "}
            <span style={{
              background: "linear-gradient(135deg,#4B6AE8,#818cf8)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>Creative Assets</span>
          </h1>
          <p style={{ fontSize: 14, color: "#6b7280", textAlign: "center", marginBottom: 40 }}>
            All animations from the audit funnel, rendered live.
          </p>

          <div className="creas-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 24,
          }}>
            {GALLERY.map((item, i) => (
              <GalleryCard key={i} title={item.title} description={item.desc}>
                {item.render()}
              </GalleryCard>
            ))}
          </div>

          {/* ── FACEBOOK ADS SECTION ── */}
          <div style={{ marginTop: 64 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#E1306C", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>Facebook & Instagram Ads</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                Animated Creatives — Feed Audit
              </h2>
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                5 variantes carrées (15s+) — Audit scan → Score → Google Shopping #1
              </p>
            </div>
            <FacebookAds/>
          </div>

          {/* ── FACEBOOK ADS V2 — BY ANGLE ── */}
          <div style={{ marginTop: 64 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>Facebook & Instagram Ads — V2</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                Animated Creatives — Par Angle
              </h2>
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                15 variantes — 5 angles (Curiosité, Douleur, Benchmark, ROAS, Ultra Court) &times; 3 créas chacun
              </p>
            </div>
            <FacebookAdsV2/>
          </div>

          {/* ── ADS CREATIVES SECTION ── */}
          <div style={{ marginTop: 64 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>Performance Max</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                Google Ads Creatives
              </h2>
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                10 creatives &times; 3 formats (1:1, 16:9, 4:5) — ready for Performance Max campaigns.
              </p>
            </div>
            <AdsCreatives/>
          </div>
        </div>
      </div>
    </>
  );
}
